import { useCallback } from "react";

import {
  AvTabState,
  PubsubVirtualClassStateMessageFromServerType,
} from "../../shared/types/av-state";
import { toastLocalizedOperationCatcher } from "../../utils/alerts";
import { useBreakouts } from "../../utils/breakouts";
import { useClassActions } from "../../utils/class-utils";

import { serverEndSession } from "api/api-server-actions";
import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { ClassSessionCategory, ClassUserType } from "components/server-types";
import { useCapabilities } from "store/capability-hooks";
import { AppDispatch, storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectActiveClassSessionId,
  selectCurrentCourseCodeTitle,
  selectCurrentCourseId,
  selectCurrentCourseTitle,
  selectIsClassSessionVirtual,
} from "store/selectors";
import { selectCurrentClassUser } from "store/selectors/class-user";
import { localStateActions } from "store/slices/local";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { useAvPubsubMessaging } from "utils/av";
import { useJoinAsCourseAdmin } from "utils/hooks/use-join-as-section-admin";
import { logInfoWithDebugFlag } from "utils/logger";
import { useQueryParamsManager } from "utils/routing";

const internalStartVirtualClass = async (courseId: string, isPractice = false) => {
  try {
    await storeApi.class_sessions.create({
      section_id: courseId,
      category: ClassSessionCategory.VIRTUAL,
      is_practice: isPractice,
    });
  } catch (e) {
    toastLocalizedOperationCatcher("virtual_class_start_failure")(e);
  }
};

const internalStopVirtualClass = async (sessionId: string, currentClassUser: ClassUserType) => {
  try {
    if (currentClassUser.is_in_class_session) {
      // First update current user's participation state to avoid race condition
      await storeApi.class_users.partial_update({
        id: currentClassUser.id,
        is_in_class_session: false,
        is_performing_av_check: false,
      });
    }

    await serverEndSession(sessionId);
  } catch (e) {
    toastLocalizedOperationCatcher("virtual_class_stop_failure")(e);
  }
};

export const useVirtualClassActions = () => {
  const currentCourseId = useAppSelector(selectCurrentCourseId);
  const currentClassSessionId = useAppSelector(selectActiveClassSessionId);
  const isClassSessionVirtual = useAppSelector(selectIsClassSessionVirtual);
  const { deleteQueryParams } = useQueryParamsManager();
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const dispatch = useAppDispatch();
  const currentCourseCodeTitle = useAppSelector(selectCurrentCourseCodeTitle);
  const currentCourseTitle = useAppSelector(selectCurrentCourseTitle);
  const { joinAsCourseAdminIfNew } = useJoinAsCourseAdmin();
  const { canAuthorCourse } = useCapabilities();

  const { onEndBreakouts } = useBreakouts();
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();
  const { updateClassUser } = useClassActions();

  const startVirtualClass = useCallback(
    async (isPractice = false) => {
      if (!currentCourseId || !canAuthorCourse) return;

      await joinAsCourseAdminIfNew();

      await internalStartVirtualClass(currentCourseId, isPractice);

      trackEvent("Virtual Class started", {
        ...GENERAL_EVENT_CATEGORY,
        courseTitle: currentCourseTitle,
        courseCode: currentCourseCodeTitle,
      });
    },
    [
      currentCourseId,
      currentCourseTitle,
      currentCourseCodeTitle,
      canAuthorCourse,
      joinAsCourseAdminIfNew,
    ],
  );

  const stopVirtualClass = useCallback(async () => {
    if (!currentClassSessionId || !currentClassUser) return;
    await internalStopVirtualClass(currentClassSessionId, currentClassUser);
    onEndBreakouts();
  }, [currentClassSessionId, currentClassUser, onEndBreakouts]);

  // AvCheck can be triggered by the user in two scenarios:
  // 1. When joining a virtual class (isJoin = true)
  // 2. When already in a virtual class and wanting to change settings (isJoin=false)
  const startAvCheck = useCallback(
    (isJoin = false) => {
      updateClassUser({
        is_performing_av_check: true,
      });
      if (isJoin) {
        // This protects against inconsistent state in the pubsub virtual class state
        // If a user can click a join class button, they should always force the join and switch A/V to the tab
        sendPubsubVirtualClassStateMessage({
          action: "join",
          force: true,
        });
      }
    },
    [updateClassUser, sendPubsubVirtualClassStateMessage],
  );

  const showAvCheckInVirtualClass = useCallback(() => {
    startAvCheck();
  }, [startAvCheck]);

  const showAvCheckToJoinVirtualClass = useCallback(
    async (isPractice = false) => {
      // Only instructors can create new sessions
      // Students should only join existing sessions
      if (canAuthorCourse && !isClassSessionVirtual) {
        // Instructor creating a new virtual session
        // Backend will set is_performing_av_check=true when session is created
        await startVirtualClass(isPractice);
      } else if (!canAuthorCourse && !isClassSessionVirtual) {
        // Student trying to join but no session exists - this shouldn't happen
        return;
      }

      // Start AV check to join the existing session
      startAvCheck(true);
    },
    [startVirtualClass, startAvCheck, isClassSessionVirtual, canAuthorCourse],
  );

  const startObservingVirtualClass = useCallback(() => {
    dispatch(PubsubVirtualClassStateActions.setObservingSessionId(currentClassSessionId));
  }, [dispatch, currentClassSessionId]);

  const stopObservingVirtualClass = useCallback(() => {
    dispatch(PubsubVirtualClassStateActions.setObservingSessionId(null));
    deleteQueryParams([FOLLOWING_QUERY_PARAM]);
  }, [dispatch, deleteQueryParams]);

  return {
    startObservingVirtualClass,
    stopObservingVirtualClass,
    showAvCheckInVirtualClass,
    showAvCheckToJoinVirtualClass,
    startVirtualClass,
    stopVirtualClass,
  };
};

export const handlePubsubVirtualClassStateMessage = (
  message: PubsubVirtualClassStateMessageFromServerType,
  dispatch: AppDispatch,
) => {
  if (message.action === "mute") {
    dispatch(localStateActions.setLocalMicIsEnabled(false));
    dispatch(localStateActions.setWasAutoMuted(false));
  } else if (message.action === "sync") {
    logInfoWithDebugFlag(
      "debug_virtual_class",
      "Received class Sync class state, updating all users",
      JSON.stringify(message),
    );
    dispatch(
      PubsubVirtualClassStateActions.updateVirtualClassState({
        users: message.data.users,
        last_updated_at: message.last_updated_at,
      }),
    );
  } else if (message.action === "already-joined") {
    console.warn(
      `Received class state message with action "reject" for user ${message.virtual_user_id},this means the user is already present in the class.`,
    );
    dispatch(PubsubVirtualClassStateActions.updateLastUpdatedAt(message.last_updated_at));
    // The tab that received this message should not be the main AV tab in any case
    dispatch(PubsubVirtualClassStateActions.updateAvTabState(AvTabState.NotMain));
  } else if (message.action === "update") {
    dispatch(
      PubsubVirtualClassStateActions.updateVirtualUserState({
        classUserId: message.virtual_user_id,
        virtualClassUserState: message.data,
        last_updated_at: message.last_updated_at,
      }),
    );
  } else {
    console.warn("Received class state message with unknown action", JSON.stringify(message));
  }
};
